import { Select, Spinner, TextInput } from '@volkswagen-onehub/components-core';
import React from 'react';
import { FORM_MESSAGE } from '../../../../constants/form-constants';
import { STYLED_INPUT } from '../../../../constants/styles-constants';
import { formService } from '../../../../form-service';
import { useFormField } from '../../../../hooks/use-form-field';
import { Option } from '../../../../types/form-types';

interface Props {
	name: string;
	label: string;
	options: Array<Option | string>;
	value?: any;
	required?: boolean;
	disabled?: boolean;
	loading?: boolean;
	onChange?: (value: any) => void;
}

export const SelectExtended = React.forwardRef<HTMLSelectElement, Props>(
	({ name, label, options, value, required, onChange, loading = true, ...props }, _ref) => {
		const normalizedOptions = options.map((option, index) => {
			if (typeof option === 'string') {
				return {
					key: index,
					label: option,
					value: option,
				};
			}

			if ('text' in option && 'value' in option) {
				return {
					key: index,
					label: option.text,
					value: typeof option.value === 'object' ? `obj_${index}` : option.value,
					object: typeof option.value === 'object' ? option.value : undefined, // Guardar el objeto real si el valor es complejo y no "object_x"
				};
			}

			const key = Object.keys(option)[0];
			return {
				key: index,
				label: option[key],
				value: key,
			};
		});

		const getSelectValue = (val: any): string => {
			if (typeof val === 'object' && val !== null) {
				// Si es objeto, encontrar la opción que lo contiene
				// Un mismo item puede llegar como instancia distinta (p. ej. salesgroups recreados en cada fetch), por eso se compara también por id
				const optionId = (option: { object?: unknown }) => (option.object as { id?: unknown } | undefined)?.id;
				return (
					normalizedOptions.find(
						(option) => option.object === val || (optionId(option) !== undefined && optionId(option) === val.id),
					)?.value || ''
				);
			}
			return val || '';
		};

		const { currentValue, validationError } = useFormField({
			name,
			value,
			required,
			onChange,
		});

		const selectedValue = getSelectValue(currentValue || value);

		const handleChange = (event: React.SyntheticEvent<HTMLSelectElement>) => {
			const selectValue = (event.target as HTMLSelectElement).value;
			// Recuperar el valor original: si tiene object usar ese sino usar value
			const selectedOption = normalizedOptions.find((option) => option.value === selectValue);
			const originalValue = selectedOption?.object ?? selectValue;
			formService.setFieldValue(name, originalValue);
		};

		const errorProps = validationError
			? {
					message: validationError === true ? FORM_MESSAGE.CHOOSE_OPTION : String(validationError),
					appearance: 'error' as const,
				}
			: {};

		return (
			<>
				<div
					className="select"
					style={{
						marginLeft: STYLED_INPUT.margin.left,
						marginRight: STYLED_INPUT.margin.right,
						marginBottom: STYLED_INPUT.margin.bottom,
						marginTop: STYLED_INPUT.margin.top,
					}}
				>
					{options.length ? (
						<Select
							{...props}
							{...errorProps}
							label={required ? label + '*' : label}
							isFloating
							onChange={handleChange}
							value={selectedValue}
						>
							<option value="" disabled>
								{label}
							</option>
							{normalizedOptions.map((option) => (
								<option key={option.key} value={option.value}>
									{option.label}
								</option>
							))}
						</Select>
					) : loading ? (
						<TextInput isFloating={false} label="" appearance="disabled" icon={<Spinner variant="default" />} />
					) : (
						<TextInput isFloating={true} label={label} appearance="disabled" />
					)}
				</div>
			</>
		);
	},
);
